import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDemoMode } from '../context/DemoModeContext.js';
import { DemoRoleSwitcher } from './DemoRoleSwitcher.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { useTour, BrandApproverTour, DistributorTour } from './Tour.js';
import { homeRouteFor } from '../lib/roleRoutes.js';

interface AppShellProps {
  children: ReactNode;
  footer?: ReactNode;
}

export function AppShell({ children, footer }: AppShellProps) {
  const { t } = useTranslation();
  const { persona } = useDemoMode();
  const navigate = useNavigate();
  const { tourActive, completeTour, retakeTour } = useTour();
  const isBrand = persona.role === 'brand_approver' || persona.role === 'brand_admin';
  const isDistributor = persona.role === 'distributor_submitter';

  return (
    <div className="min-h-screen flex flex-col bg-bone-soft">
      <header className="sticky top-0 z-40 bg-bone-soft border-b border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Go to home"
              onClick={() => navigate(homeRouteFor(persona.role))}
              className="cursor-pointer"
            >
              <img src="/logo.svg" alt="Infoscend" className="h-7 w-auto" />
            </button>
            <span className="text-slate-500 text-sm font-medium hidden sm:inline">
              {t('app.demoSubtitle')}
            </span>
          </div>

          <div className="flex items-center gap-4">
            {persona.role === 'brand_admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="text-sm font-medium text-ink hover:text-copper transition-colors"
              >
                {t('admin.title')}
              </button>
            )}
            {(isBrand || isDistributor) && (
              <button
                type="button"
                onClick={retakeTour}
                className="text-xs font-medium text-slate-500 hover:text-copper transition-colors border border-hairline rounded px-2 py-1"
              >
                {t('tour.retakeTour', { defaultValue: 'Take Tour' })}
              </button>
            )}
            <LanguageSwitcher />
            <div className="w-px h-4 bg-slate-200" />
            <DemoRoleSwitcher />
          </div>
        </div>

        <div className="border-t border-hairline bg-copper-soft px-6 py-1.5 text-center">
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-copper-deep">
            {t('roleSwitcher.currentRole')}: <strong>{t(`roles.${persona.role}`)}</strong>
            {' — '}{persona.displayName} ({persona.orgName})
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-[1200px] mx-auto w-full px-6 py-8">
        {children}
        {footer}
      </main>

      {isBrand && (
        <BrandApproverTour tourActive={tourActive} onFinish={completeTour} />
      )}
      {isDistributor && (
        <DistributorTour tourActive={tourActive} onFinish={completeTour} />
      )}
    </div>
  );
}
